// ============================================================
// DefendantSection.tsx
// Redesign (final sections pass): the shared SectionCards view/edit cards
// in the case shell (SELF_FRAMED) — Defendant (name · reg · phone) and
// Address — with the glance strip and the shared section footer.
//
// Not PartyContactSection: the defendant lives in the nested
// currentCase.defendant object ({ reg, name, address: Address|null,
// mainContactNumber }) with a REQUIRED name and a reg plate, and has no
// email / notes — different shape from the flat medical/TPS columns.
//
// LOGIC UNCHANGED:
//   • every input writes ONE field of currentCase.defendant via
//     updateCase({ defendant: { ...live, [field]: value } }) → debounced
//     case autosave; reg + postcode uppercased; same fieldKeys
//     (defendant.reg / name / address-line-1 … postcode /
//     main-contact-number / address-search) for presence + live sync
//   • address helper treats null as all-empty and overlays the change;
//     AddressLookup select/clear writes all 5 in ONE update +
//     broadcastFieldEdits(DEFENDANT_ADDRESS_FIELD_KEYS)
//   • "Defendant name is required" gates Mark complete (was validated at
//     the button; now the shared footer shows/greys it — same rule)
//   • markComplete('defendant', next) + advanceCaseSection (SectionFooter)
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import type { Defendant, Address } from '../../types/case.types'
import FormField from '../shared/FormField'
import PhoneLink, { PHONE_GLANCE_LINK } from '../shared/PhoneLink'
import FieldError from '../shared/FieldError'
import AddressLookup from '../shared/AddressLookup'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import type { ValidationError } from '../../utils/validation'
import { inpCls, FGRID } from '../clientInfo/clientInfoRegistry'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import { KIcon, Glance, GlanceDash, SectionFooter, Kv, Empty, AddressBlock, DL, BIG, MONO, PLATE, LINK } from '../case/sections/sectionKit'

// Live edit-sync: address-lookup writes all 5 address fields programmatically.
const DEFENDANT_ADDRESS_FIELD_KEYS = ['defendant.address-line-1', 'defendant.address-line-2', 'defendant.town', 'defendant.county', 'defendant.postcode']
const ADDRESS_SENTINEL = 'defendant.__address'
const FIELD_KEY: Record<keyof Defendant, string> = {
  reg: 'defendant.reg',
  name: 'defendant.name',
  mainContactNumber: 'defendant.main-contact-number',
  address: ADDRESS_SENTINEL, // Cancel restores the whole object → broadcast all 5
}
const broadcast = (keys: string[]): void =>
  broadcastFieldEdits(keys.flatMap((k) => (k === ADDRESS_SENTINEL ? DEFENDANT_ADDRESS_FIELD_KEYS : [k])))
const EMPTY_ADDR: Address = { line1: '', line2: '', town: '', county: '', postcode: '' }

export const addressLinesOf = (a: Address | null): { lines: string[]; postcode: string } => ({
  lines: a ? [a.line1, a.line2, [a.town, a.county].filter((s) => s?.trim()).join(', ')].map((s) => (s ?? '').trim()).filter(Boolean) : [],
  postcode: (a?.postcode ?? '').trim(),
})

const DefendantSection: React.FC = () => {
  const { currentCase, updateCase } = useCasevoStore()
  if (!currentCase) return null
  const defendant = currentCase.defendant
  const liveDef = (): Defendant => useCasevoStore.getState().currentCase?.defendant ?? defendant
  const update = (field: keyof Defendant, value: unknown): void => {
    updateCase({ defendant: { ...liveDef(), [field]: value } })
  }
  const updateMany = (changes: Partial<Defendant>): void => {
    updateCase({ defendant: { ...liveDef(), ...changes } })
  }
  // Address helper — single updateCase per keystroke, treating null as
  // all-empty and overlaying the changed sub-field.
  const updateAddress = (changes: Partial<Address>): void => {
    const a = liveDef().address
    update('address', { ...EMPTY_ADDR, ...(a ?? {}), ...changes })
  }

  const errors: ValidationError[] = defendant.name.trim() ? [] : [{ field: 'name', message: 'Defendant name is required' }]
  const addr = addressLinesOf(defendant.address)

  const text = (
    ctx: { idPrefix: string }, id: string, label: string, value: string, onChange: (v: string) => void, fieldKey: string,
    opts: { type?: string; placeholder?: string; full?: boolean; err?: string; required?: boolean; mono?: boolean } = {},
  ): React.ReactNode => (
    <div className={opts.full ? 'sm:col-span-2' : ''}>
      <FormField variant="info" label={label} fieldKey={fieldKey} htmlFor={`${ctx.idPrefix}-${id}`} required={opts.required}>
        <input id={`${ctx.idPrefix}-${id}`} type={opts.type ?? 'text'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={opts.placeholder}
          aria-invalid={opts.err ? true : undefined} className={`${inpCls(!!opts.err)} ${opts.mono ? 'font-mono tracking-[0.06em]' : ''}`} />
        <FieldError message={opts.err} />
      </FormField>
    </div>
  )

  const defs: CardDef<Defendant>[] = [
    {
      key: 'defendant', title: 'Defendant', sub: 'Who caused the accident', icon: <KIcon name="shield" size={17} />,
      fields: ['name', 'reg', 'mainContactNumber'], errorKeys: ['name'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Name" full>{defendant.name.trim() ? <span className={BIG}>{defendant.name}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Reg">{defendant.reg.trim() ? <span className={PLATE}>{defendant.reg}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Main contact number">{defendant.mainContactNumber.trim() ? <PhoneLink value={defendant.mainContactNumber} className={LINK} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          {text(ctx, 'name', 'Name', defendant.name, (v) => update('name', v), 'defendant.name', { full: true, required: true, err: ctx.err('name') })}
          {text(ctx, 'reg', 'Reg', defendant.reg, (v) => update('reg', v.toUpperCase()), 'defendant.reg', { placeholder: 'Enter registration', mono: true })}
          {text(ctx, 'phone', 'Main contact number', defendant.mainContactNumber, (v) => update('mainContactNumber', v), 'defendant.main-contact-number', { type: 'tel', placeholder: '07700 900000' })}
        </div>
      ),
    },
    {
      key: 'address', title: 'Address', sub: 'Postal address', icon: <KIcon name="map" size={17} />,
      fields: ['address'],
      presenceKeys: [...DEFENDANT_ADDRESS_FIELD_KEYS, 'defendant.address-search'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Address" full>{addr.lines.length || addr.postcode ? <AddressBlock lines={addr.lines} postcode={addr.postcode || undefined} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => {
        const a = defendant.address ?? EMPTY_ADDR
        return (
          <div className={FGRID}>
            <div className="sm:col-span-2">
              <FormField variant="info" label="Search address" fieldKey="defendant.address-search" htmlFor={`${ctx.idPrefix}-search`}>
                <AddressLookup
                  variant="info"
                  inputId={`${ctx.idPrefix}-search`}
                  value={defendant.address}
                  onSelect={(picked) => { updateAddress(picked); broadcastFieldEdits(DEFENDANT_ADDRESS_FIELD_KEYS) }}
                  onClear={() => { updateAddress(EMPTY_ADDR); broadcastFieldEdits(DEFENDANT_ADDRESS_FIELD_KEYS) }}
                />
              </FormField>
            </div>
            {text(ctx, 'l1', 'Address line 1', a.line1, (v) => updateAddress({ line1: v }), 'defendant.address-line-1')}
            {text(ctx, 'l2', 'Address line 2', a.line2, (v) => updateAddress({ line2: v }), 'defendant.address-line-2')}
            {text(ctx, 'town', 'Town', a.town, (v) => updateAddress({ town: v }), 'defendant.town')}
            {text(ctx, 'county', 'County', a.county, (v) => updateAddress({ county: v }), 'defendant.county')}
            {text(ctx, 'pc', 'Postcode', a.postcode, (v) => updateAddress({ postcode: v.toUpperCase() }), 'defendant.postcode', { placeholder: 'LS6 3AB', mono: true })}
          </div>
        )
      },
    },
  ]

  const a = defendant.address
  const glance = (
    <Glance cells={[
      { icon: 'shield', label: 'Defendant', value: defendant.name.trim() || <GlanceDash />, sub: defendant.name.trim() ? 'Name' : 'Required' },
      { icon: 'car', label: 'Reg', value: defendant.reg.trim() ? <span className={`${MONO} !text-[13px]`}>{defendant.reg}</span> : <GlanceDash />, sub: defendant.reg.trim() ? 'Vehicle registration' : 'Not recorded' },
      { icon: 'phone', label: 'Phone', value: defendant.mainContactNumber.trim() ? <PhoneLink value={defendant.mainContactNumber} className={PHONE_GLANCE_LINK} size="sm" /> : <GlanceDash />, sub: defendant.mainContactNumber.trim() ? 'Main contact number' : 'Not recorded' },
      {
        icon: 'map', label: 'Based in',
        value: a && (a.town.trim() || a.postcode.trim()) ? <>{a.town}{a.town.trim() && a.postcode.trim() ? ' · ' : ''}{a.postcode.trim() && <span className={`${MONO} !text-[13px]`}>{a.postcode}</span>}</> : <GlanceDash />,
        sub: a?.line1.trim() || 'Address',
      },
    ]} />
  )

  return (
    <SectionCards<Defendant>
      value={defendant}
      onChange={updateMany}
      fieldKeyOf={(f) => FIELD_KEY[f]}
      onBroadcast={broadcast}
      onFlush={flushAutoSave}
      errors={errors}
      defs={defs}
      title="Defendant"
      sub="The other party: the driver or company responsible."
      glance={glance}
      footer={<SectionFooter sectionKey="defendant" errors={errors} />}
      idPrefix={`def-${currentCase.id}`}
    />
  )
}
export default DefendantSection
